import { Injectable, inject } from '@angular/core';
import { Firestore, collection, doc, increment, writeBatch } from '@angular/fire/firestore';
import { usagePaths } from 'src/app/domain/governance/types/usage.schema';
import { AccountState } from 'src/app/store/account';
import { AuthState } from 'src/app/store/auth';

/**
 * V2 usage-event codes the compat surfaces log. Matches the codes V2's
 * MetricsService derives from event names, so V2-origin activity (mirrored by
 * `scripts/sync-v2-usage.ts`) and V3-native activity land in the same
 * vocabulary and aggregate cleanly on the Insights Usage tab.
 */
export type V2UsageEventCode = 'goal_and_objectives_created' | 'observational_note_created';

/**
 * Records compat usage activity into the PARTNER-SCOPED usage collections
 * (`src/app/domain/governance/types/usage.schema.ts`), porting V2's
 * `MetricsService.log()` write shape: one batched write incrementing the
 * team counter and the acting user's counter, plus an event doc for the
 * timeline.
 *
 * **Writes land in `v3Native`, never `v2Mirror`.** The mirror is owned
 * exclusively by the sync script, which recomputes it wholesale from the
 * still-live V2 `domains/**` docs on every run. Keeping the two sets disjoint
 * is what lets the mirror be overwritten idempotently while native counters
 * accrue underneath it — no double counting, and nothing to reconcile when V2
 * is finally retired (the mirror simply freezes).
 *
 * Firestore paths (all member-scoped):
 *   - `partners/{partnerId}/usage/summary`      `v3Native.{code}` += 1
 *   - `partners/{partnerId}/usage_users/{uid}`  `v3Native.{code}` += 1
 *   - `partners/{partnerId}/usage_events/{auto}` `{…, source: 'v3'}`
 *
 * Writes use set-with-merge so a first-time user needs no pre-existing doc.
 * Blume staff activity is never logged (V2 parity). Events carry staff
 * uid/email only — NEVER student data or export content.
 *
 * @security FERPA — never pass student PII in `eventName` or any field.
 */
@Injectable({ providedIn: 'root' })
export class UsageMetricsService {
  private readonly firestore = inject(Firestore);
  private readonly accountState = inject(AccountState);
  private readonly authState = inject(AuthState);

  /**
   * Log one usage event. Fire-and-forget at call sites — failures are
   * logged and swallowed so telemetry can never break a user flow.
   */
  async log(code: V2UsageEventCode, eventName: string): Promise<void> {
    try {
      if (this.accountState._isVerifiedBlumeAdmin()) return;

      const email = this.accountState.accountEmail()?.toLowerCase() ?? null;
      const uid = this.authState.userId();
      const partnerId = this.accountState.activeTeam()?.id ?? null;
      if (!email || !uid || !partnerId) return;

      const timestamp = Date.now();
      const batch = writeBatch(this.firestore);
      batch.set(
        doc(this.firestore, usagePaths.summary(partnerId)),
        { v3Native: { [code]: increment(1) } },
        { merge: true },
      );
      batch.set(
        doc(this.firestore, usagePaths.user(partnerId, uid)),
        { uid, email, v3Native: { [code]: increment(1) } },
        { merge: true },
      );
      batch.set(doc(collection(this.firestore, usagePaths.events(partnerId))), {
        eventName,
        code,
        uid,
        email,
        timestamp,
        source: 'v3',
      });
      await batch.commit();
    } catch (error) {
      // Telemetry only — never surface to the user, never log PII.
      console.error('[UsageMetricsService] failed to log usage event', code, error);
    }
  }
}
